import React, { useEffect, useState } from 'react';
import Typography from '../../../basic/Typography';
import { NewNGJob } from '../types';
import { newNGApi } from '../services/newNGApi';
import styles from '../NewNG.module.css';

interface NewsScheduleProps {
  currentJob?: NewNGJob | null;
  onJobSelect?: (job: NewNGJob) => void;
}

export const NewsSchedule: React.FC<NewsScheduleProps> = ({ currentJob, onJobSelect }) => {
  const [pastJobs, setPastJobs] = useState<NewNGJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Format time from ISO string to display format
  const formatTime = (dateString?: string) => {
    if (!dateString) return '--:--';
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return '--:--';
    }
  };



  // Store enhanced job info
  const [currentJobInfo, setCurrentJobInfo] = useState<any>(null);
  const [pastJobsInfo, setPastJobsInfo] = useState<any[]>([]);

  // Generate show name based on enhanced job info
  const generateShowName = (job: NewNGJob, enhancedInfo?: any, index?: number) => {
    // Use script title if available
    if (enhancedInfo?.script?.show_metadata?.title) {
      return enhancedInfo.script.show_metadata.title;
    }

    // Use query topic if available
    if (enhancedInfo?.metadata?.query?.topic) {
      return enhancedInfo.metadata.query.topic;
    }

    // Use job title if available
    if (job.title) return job.title;

    // Generate appropriate show names
    if (index !== undefined) {
      const showTypes = [
        'News Update', 'Breaking News', 'News Briefing', 'News Report',
        'Current Affairs', 'News Summary', 'Daily News', 'News Roundup'
      ];
      return `${showTypes[index % showTypes.length]}`;
    }

    return `News Video ${job.job_id.slice(0, 8)}`;
  };

  // Format air date (same logic as More News cards)
  const formatAirDate = (dateString?: string) => {
    if (!dateString) return '';

    try {
      const airDate = new Date(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - airDate.getTime()) / (1000 * 60 * 60);

      let timeAgo = '';
      if (diffInHours < 1) {
        timeAgo = 'Less than 1 hour ago';
      } else if (diffInHours < 24) {
        timeAgo = `${Math.floor(diffInHours)} hours ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) {
          timeAgo = `${diffInDays} days ago`;
        } else if (diffInDays < 30) {
          const diffInWeeks = Math.floor(diffInDays / 7);
          timeAgo = `${diffInWeeks} weeks ago`;
        } else {
          const diffInMonths = Math.floor(diffInDays / 30);
          timeAgo = `${diffInMonths} months ago`;
        }
      }

      const formattedDate = airDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      return `${timeAgo}, ${formattedDate}`;
    } catch (error) {
      console.warn('Error formatting air date:', error);
      return '';
    }
  };

  // Generate description based on enhanced job info
  const generateDescription = (job: NewNGJob, enhancedInfo?: any) => {
    const parts = [];

    // Add air date first (like More News cards)
    const airDate = enhancedInfo?.metadata?.created_at || job.created_at;
    if (airDate) {
      const formattedAirDate = formatAirDate(airDate);
      if (formattedAirDate) {
        parts.push(`Aired: ${formattedAirDate}`);
      }
    }

    // Add script-based info
    if (enhancedInfo?.script) {
      const sceneCount = enhancedInfo.script.scenes?.length || 0;
      const duration = enhancedInfo.script.total_duration_sec;

      if (sceneCount > 0) {
        parts.push(`${sceneCount} scenes`);
      }

      if (duration) {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        parts.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }

    // Add query info
    if (enhancedInfo?.metadata?.query) {
      const query = enhancedInfo.metadata.query;
      if (query.location && query.location !== 'global') {
        parts.push(query.location);
      }
      // Remove timerange info as it's redundant with air date
      // if (query.timerange) {
      //   parts.push(`${query.timerange} news`);
      // }
    }

    // Add event count
    if (enhancedInfo?.metadata?.results?.total_events) {
      parts.push(`${enhancedInfo.metadata.results.total_events} events`);
    }

    // Fallback to job info
    if (parts.length === 0) {
      if (job.description) {
        return job.description.substring(0, 60) + (job.description.length > 60 ? '...' : '');
      }
      const fallbackAirDate = formatAirDate(job.created_at);
      if (fallbackAirDate) {
        parts.push(`Aired: ${fallbackAirDate}`);
      }
      parts.push(`Job ID: ${job.job_id.slice(0, 8)}`);
      parts.push(job.overall_status || job.status || 'Completed');
    }

    return parts.join(' • ');
  };

  // Load enhanced information for current job
  useEffect(() => {
    const loadCurrentJobInfo = async () => {
      if (!currentJob?.job_id) {
        setCurrentJobInfo(null);
        return;
      }

      try {

        const enhancedInfo = await newNGApi.getEnhancedJobInfo(currentJob.job_id);
        setCurrentJobInfo(enhancedInfo);

      } catch (error) {
        console.error('Failed to load enhanced current job info:', error);
        setCurrentJobInfo(null);
      }
    };

    loadCurrentJobInfo();
  }, [currentJob?.job_id]);

  // Load past jobs with enhanced information
  useEffect(() => {
    const loadPastJobs = async () => {
      try {
        setIsLoading(true);

        // Fetch all jobs
        const allJobs = await newNGApi.getJobs();

        // Filter and sort jobs that are older than current job
        let filteredJobs = allJobs;

        if (currentJob?.created_at) {
          const currentJobTime = new Date(currentJob.created_at).getTime();
          filteredJobs = allJobs.filter(job => {
            if (!job.created_at) return false;
            return new Date(job.created_at).getTime() < currentJobTime;
          });
        }

        // Sort by creation date (newest first) and take top 15
        const sortedPastJobs = filteredJobs
          .sort((a, b) => {
            const dateA = new Date(a.created_at || '').getTime();
            const dateB = new Date(b.created_at || '').getTime();
            return dateB - dateA; // Descending order (newest first)
          })
          .slice(0, 15);

        setPastJobs(sortedPastJobs);

        // Load enhanced information for past jobs (in parallel, but limit to avoid overwhelming)
        const enhancedInfoPromises = sortedPastJobs.slice(0, 12).map(async (job) => {
          try {
            const enhancedInfo = await newNGApi.getEnhancedJobInfo(job.job_id);
            return { job, enhancedInfo };
          } catch (error) {
            console.warn(`Failed to load enhanced info for job ${job.job_id}:`, error);
            return { job, enhancedInfo: null };
          }
        });

        const enhancedResults = await Promise.all(enhancedInfoPromises);
        setPastJobsInfo(enhancedResults);



      } catch (error) {
        console.error('Failed to load past jobs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPastJobs();
  }, [currentJob]);
  return (
    <div className={styles['newng-schedule-panel']}>
      <h2 className={styles['newng-section-title']}>SCHEDULE</h2>

      <div className={styles['newng-schedule-columns']}>
        <div className={styles['newng-schedule-column']}>
          <div className={styles['newng-schedule-section']}>
            <Typography variant="news-subtitle" component="div" className={styles['newng-schedule-section-title']}>Upcoming</Typography>
            <div className={styles['newng-schedule-items']}>
              <div className={styles['newng-schedule-item']}>
                <Typography variant="news-time" component="div" className={styles['newng-schedule-time']}>23:30</Typography>
                <div className={styles['newng-schedule-content']}>
                  <Typography variant="news-body" component="div" className={styles['newng-schedule-title']}>Business Today</Typography>
                  <Typography variant="body1" component="div" className={styles['newng-schedule-description']}>Latest business news and market analysis</Typography>
                </div>
              </div>
              <div className={styles['newng-schedule-item']}>
                <Typography variant="news-time" component="div" className={styles['newng-schedule-time']}>00:00</Typography>
                <div className={styles['newng-schedule-content']}>
                  <Typography variant="news-body" component="div" className={styles['newng-schedule-title']}>Late Night News</Typography>
                  <Typography variant="body1" component="div" className={styles['newng-schedule-description']}>Late breaking news and updates</Typography>
                </div>
              </div>
            </div>
          </div>

          <div className={styles['newng-schedule-section']}>
            <Typography variant="news-subtitle" component="div" className={styles['newng-schedule-section-title']}>Now Playing</Typography>
        <div className={styles['newng-schedule-items']}>
          {currentJob ? (
            <div className={`${styles['newng-schedule-item']} ${styles['now-playing']}`}>
              <Typography variant="news-time" component="div" className={styles['newng-schedule-time']}>
                {formatTime(currentJob.created_at)}
              </Typography>
              <div className={styles['newng-schedule-content']}>
                    <Typography variant="news-body" component="div" className={styles['newng-schedule-title']}>
                  {generateShowName(currentJob, currentJobInfo)}
                </Typography>
                    <Typography variant="body1" component="div" className={styles['newng-schedule-description']}>
                  {generateDescription(currentJob, currentJobInfo)}
                </Typography>
              </div>
            </div>
          ) : (
            <div className={`${styles['newng-schedule-item']} ${styles['now-playing']}`}>
              <Typography variant="news-time" component="div" className={styles['newng-schedule-time']}>--:--</Typography>
              <div className={styles['newng-schedule-content']}>
                      <Typography variant="news-body" component="div" className={styles['newng-schedule-title']}>Loading...</Typography>
                      <Typography variant="body1" component="div" className={styles['newng-schedule-description']}>Preparing news content</Typography>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>

        <div className={styles['newng-schedule-column']}>
          <div className={styles['newng-schedule-section']}>
            <Typography variant="news-subtitle" component="div" className={styles['newng-schedule-section-title']}>Past Videos</Typography>
        <div className={styles['newng-schedule-items']}>
          {isLoading ? (
            // Loading state
                Array.from({ length: 12 }, (_, i) => (
              <div key={i} className={styles['newng-schedule-item']}>
                <div className={styles['newng-schedule-time']}>--:--</div>
                <div className={styles['newng-schedule-content']}>
                  <div className={styles['newng-schedule-title']}>Loading...</div>
                  <div className={styles['newng-schedule-description']}>Loading past videos...</div>
                </div>
              </div>
            ))
          ) : pastJobsInfo.length > 0 ? (
            // Show past jobs with enhanced info
            pastJobsInfo.map(({ job, enhancedInfo }, index) => (
              <div
                key={job.job_id}
                className={`${styles['newng-schedule-item']} ${onJobSelect ? styles['newng-schedule-item--clickable'] : ''}`}
                onClick={() => onJobSelect?.(job)}
                style={{ cursor: onJobSelect ? 'pointer' : 'default' }}
              >
                <div className={styles['newng-schedule-time']}>{formatTime(job.created_at)}</div>
                <div className={styles['newng-schedule-content']}>
                  <div className={styles['newng-schedule-title']}>{generateShowName(job, enhancedInfo, index)}</div>
                  <div className={styles['newng-schedule-description']}>
                    {generateDescription(job, enhancedInfo)}
                  </div>
                </div>
              </div>
            ))
            ) : pastJobs.length > 0 ? (
              // Fallback: Show past jobs without enhanced info
              pastJobs.map((job, index) => (
                <div
                  key={job.job_id}
                  className={`${styles['newng-schedule-item']} ${onJobSelect ? styles['newng-schedule-item--clickable'] : ''}`}
                  onClick={() => onJobSelect?.(job)}
                  style={{ cursor: onJobSelect ? 'pointer' : 'default' }}
                >
                  <div className={styles['newng-schedule-time']}>{formatTime(job.created_at)}</div>
                  <div className={styles['newng-schedule-content']}>
                    <div className={styles['newng-schedule-title']}>{generateShowName(job, null, index)}</div>
                    <div className={styles['newng-schedule-description']}>
                      {generateDescription(job, null)}
                    </div>
                  </div>
                </div>
              ))
              ) : (
                // No past jobs available
                  <div className={styles['newng-schedule-item']}>
                    <div className={styles['newng-schedule-time']}>--:--</div>
                    <div className={styles['newng-schedule-content']}>
                      <div className={styles['newng-schedule-title']}>No past videos</div>
                      <div className={styles['newng-schedule-description']}>No previous news videos available</div>
                    </div>
                  </div>
          )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
